import { useMediaReview } from '../../hooks/useMediaReview'
import { useExecutionText } from '../../hooks/useExecutionText'
import React, { useState, useEffect, useMemo } from 'react'
import { RootState, Subtask } from 'constants/interfaces'
import { useResults } from 'components/TasksV2/hooks/useResults'
import { MediaSubtaskCompleting } from './MediaSubtaskCompleting'
import { ExecutionReviewActions } from '../../components/ExecutionReviewActions'
import { ReservationsModal } from './ReservationsModal'
import { Box, Flex, Tag, Text } from '@chakra-ui/core'
import { useSelector } from 'react-redux'
import keys from 'constants/keys'
import { StatusTagWithRating } from 'components/TasksV2/UI/StatusTagWithRating'
import { getMediaCid } from 'utils'
import { isNumber } from 'lodash'
import { AlertCircle } from 'lucide-react'
import useLocaleText from 'components/useLocaleText'
import { CHART_COLORS } from 'utils/taskAnalytics'
import { getReasonsByRetailer } from 'components/TasksV2/UI/ReasonComponents/reasonsConfig'

interface IProps {
  embedded?: boolean
  executionFooter?: React.ReactNode
  subtask: Subtask
  isReadMode: boolean
  resultContextId: string
  retail_id_context_id: string
  recurrence_ts: string | null
  is_required_approval?: boolean
  initialMediaIndex?: number | null
  targetParentCommentId?: string | null
  targetCommentId?: string | null
}

const ModalMediaSection = ({
  embedded,
  executionFooter,
  subtask,
  isReadMode,
  resultContextId,
  retail_id_context_id,
  recurrence_ts,
  is_required_approval,
  initialMediaIndex,
  targetParentCommentId,
  targetCommentId,
}: IProps) => {
  const { type, tid, st_id } = subtask
  const subTaskResult = useResults({ subtask, resultContextId, recurrence_ts })
  const { cdn, rid } = useSelector((state: RootState) => state.config.config)
  const rework_limit_desc = useLocaleText('rework_limit_desc')
  const rework_count = useLocaleText('rework_count')
  const t = useExecutionText()
  const approveWithReservationReasons = useMemo(() => getReasonsByRetailer(rid, 'approve_with_reservation'), [rid])
  const rejectReasons = useMemo(() => getReasonsByRetailer(rid, 'reject'), [rid])
  const {
    isApproved,
    isRejected,
    rating,
    ratingFeedback,
    reservations,
    isReservationsModalOpen,
    reservationsMode,
    busy,
    error,
    reviewFeedback,
    canReview,
    currentReworkCount,
    reworkLimit,
    isReworkLimitReached,
    handleApprove,
    handleReject,
    handleApproveWithReservation,
    handleReservationsConfirm,
    handleRating,
    closeReservations,
  } = useMediaReview(subtask, subTaskResult, isReadMode)

  const subtaskWithUuid = {
    ...subtask,
    st_id: subtask.st_id,
    type: subtask.type,
    status: 0,
    title: subtask.title,
    context_id: resultContextId,
    uuid: `${st_id}_${resultContextId}_${recurrence_ts ?? ''}`,
  }
  const mediaURLs = useMemo(() => [...(subTaskResult?.images ?? []), ...(subTaskResult?.videos ?? [])], [subTaskResult])
  const resolvedInitialMediaIndex = useMemo(() => {
    if (isNumber(initialMediaIndex)) return initialMediaIndex
    if (!targetParentCommentId) return null

    const targetIndex = mediaURLs.findIndex((url) => getMediaCid({ url, cdn, rid }) === targetParentCommentId)
    return targetIndex >= 0 ? targetIndex : null
  }, [cdn, initialMediaIndex, mediaURLs, rid, targetParentCommentId])
  const [currentMediaIndex, setCurrentMediaIndex] = useState(resolvedInitialMediaIndex ?? 0)

  const onMediaChange = (index: number) => {
    setCurrentMediaIndex(index)
  }

  // Only calculate approval/disapproval status if is_required_approval is true
  const isSubtaskApproved =
    subTaskResult?.status === keys.TASK_STATUSES.APPROVED.value ||
    subTaskResult?.status === keys.TASK_STATUSES.APPROVED_WITH_RESERVATION.value
  const isSubtaskDisapproved = subTaskResult?.status === keys.TASK_STATUSES.DISAPPROVED.value

  useEffect(() => {
    if (resolvedInitialMediaIndex === null || resolvedInitialMediaIndex === undefined) return
    setCurrentMediaIndex(resolvedInitialMediaIndex)
  }, [resolvedInitialMediaIndex])

  return (
    <Flex id="media-subtask-completing" flexDir="column" flex={1} minH={0} minW={0}>
      {/* Media Subtask Completing */}
      <MediaSubtaskCompleting
        splitView
        reviewFeedback={reviewFeedback}
        embedded={embedded}
        mediaFooter={
          <>
            {/* Approve/Reject/Rating Buttons - только если is_required_approval === true и пользователь в списке auditors */}
            {canReview && (
              <>
                <ExecutionReviewActions
                  rating={rating}
                  ratingFeedback={ratingFeedback}
                  isApproved={isApproved}
                  isRejected={isRejected}
                  onRating={handleRating}
                  onApprove={handleApprove}
                  disabled={busy}
                  onReject={handleReject}
                  rejectBlocked={isReworkLimitReached && !isRejected}
                  onApproveWithReservation={
                    approveWithReservationReasons.length ? handleApproveWithReservation : undefined
                  }
                />
              </>
            )}

            {!isReadMode && (isSubtaskApproved || isSubtaskDisapproved) && (
              <Box mb="10px">
                <StatusTagWithRating
                  status={subTaskResult?.status ?? 0}
                  rating={subTaskResult?.rating ?? 0}
                  size="small"
                />
              </Box>
            )}

            {error && (
              <Text role="alert" color="red.500">
                {t.saveFailed}
              </Text>
            )}
            {executionFooter && <Flex justifyContent="flex-end">{executionFooter}</Flex>}
          </>
        }
        mediaHeader={
          <>
            {/* Rework Count Info */}
            {!isReworkLimitReached && isNumber(reworkLimit) && reworkLimit > 0 && currentReworkCount > 0 && (
              <Tag bg="#FFF3CD" border="1px solid #FFC107" borderRadius="6px" p="10px" mb="15px" width="100%">
                <Text fontSize="14px" color="#856404" fontWeight="600">
                  {rework_count} {currentReworkCount}/{reworkLimit}
                </Text>
              </Tag>
            )}

            {/* Rework Limit Reached Warning */}
            {isReworkLimitReached && (
              <Box
                mb="12px"
                p="10px"
                bg={CHART_COLORS.rejected}
                borderRadius="6px"
                border={`1px solid ${CHART_COLORS.rejectedText}`}
              >
                <Flex alignItems="center" style={{ gap: '6px' }}>
                  <AlertCircle size={16} color={CHART_COLORS.rejectedText} />
                  <Text fontSize="12px" fontWeight="600" color={CHART_COLORS.rejectedText}>
                    {rework_limit_desc} ({currentReworkCount}/{reworkLimit})
                  </Text>
                </Flex>
              </Box>
            )}
          </>
        }
        images={subTaskResult?.images ?? []}
        videos={subTaskResult?.videos ?? []}
        subtask={subtaskWithUuid}
        retail_id_context_id={retail_id_context_id}
        recurrence_ts={recurrence_ts}
        resultContextId={resultContextId}
        isReadMode={isReadMode}
        mediaType={type}
        onMediaChange={onMediaChange}
        currentMediaIndex={currentMediaIndex}
        tid={tid}
        isReworkLimitReached={isReworkLimitReached}
        targetParentCommentId={targetParentCommentId}
        targetCommentId={targetCommentId}
      />

      {/* Reservations Modal - only if is_required_approval is true */}
      {is_required_approval && (
        <ReservationsModal
          isOpen={isReservationsModalOpen}
          isSaving={busy}
          error={error ? t.saveFailed : undefined}
          onClose={closeReservations}
          onConfirm={handleReservationsConfirm}
          mode={reservationsMode}
          initialRating={rating}
          initialReservations={reservations}
          predefinedReasons={reservationsMode === 'reject' ? rejectReasons : approveWithReservationReasons}
        />
      )}
    </Flex>
  )
}

export default ModalMediaSection
